import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Logo from '../components/logo.jsx';
import { 
    UserRound, 
    KeyRound, 
    ArrowRight,
    AlertCircle,
    Eye,
    EyeOff,
    Sparkles,
    Zap,
    Shield
} from 'lucide-react';
import { API_BASE_URL } from "../config/apiconfig";

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [needsVerification, setNeedsVerification] = useState(false);
    const [resentMsg, setResentMsg] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [focusedField, setFocusedField] = useState('');
    const navigate = useNavigate();
    const { login, user } = useAuth();

    // If already authenticated, redirect away from login immediately
    useEffect(() => {
        if (user) {
            let to = '/home';
            if (user.role === 'e2e-admin') to = '/admin-dashboard';
            else if (user.role === 'recruiter') to = '/recruiter-dashboard';
            else if (user.role === 'company-admin') to = '/company-admin';
            else if (user.role === 'cluster-admin') to = '/cluster-admin';
            else if (user.role === 'unit-admin') to = '/unit-admin';
            navigate(to, { replace: true });
        }
    }, [user, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setNeedsVerification(false);
        setResentMsg('');

        if (!email.trim() || !password.trim()) {
            setError('Please fill in all fields');
            setIsLoading(false);
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/login`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    email: email.trim(), 
                    password: password 
                }),
            });

            const data = await response.json();
            
            if (response.ok) {
                localStorage.setItem('token', data.token);
                login(data.user);
                
                switch (data.user.role) {
                    case 'e2e-admin':
                        navigate('/admin-dashboard', { replace: true });
                        break;
                    case 'company-admin':
                        navigate('/company-admin', { replace: true });
                        break;
                    case 'cluster-admin':
                        navigate('/cluster-admin', { replace: true });
                        break;
                    case 'unit-admin':
                        navigate('/unit-admin', { replace: true });
                        break;
                    case 'recruiter': {
                        try {
                            const pr = await fetch(`${API_BASE_URL}/api/recruiter/profile/${data.user.id}?_=${Date.now()}`);
                            if (pr.ok) {
                                const prof = await pr.json();
                                const complete = (typeof prof.onboarding_completed !== 'undefined')
                                    ? !!prof.onboarding_completed
                                    : !!(prof.first_name && prof.last_name && prof.work_email && prof.phone && prof.company_id);
                                if (!complete) {
                                    navigate('/recruiter-onboarding', { replace: true });
                                    break;
                                }
                                if (prof?.approval_status !== 'approved') {
                                    navigate('/waiting-approval', { replace: true });
                                    break;
                                }
                                if (prof?.account_status !== 'active') {
                                    navigate('/inactive-account', { replace: true });
                                    break;
                                }
                            } else {
                                // No recruiter row yet: go to onboarding
                                navigate('/recruiter-onboarding', { replace: true });
                                break;
                            }
                        } catch {}
                        navigate('/recruiter-dashboard', { replace: true });
                        break;
                    }
                    default:
                        navigate('/home', { replace: true });
                }
            } else {
                if (data?.rejected) {
                    setError(data.error || 'Your account request was rejected by an admin.');
                } else if (data?.needsVerification) {
                    setNeedsVerification(true);
                    setError(data.error || 'Email is not verified. Please check your inbox.');
                } else {
                    setError(data.error || 'Login failed. Please try again.');
                }
            }
        } catch (error) {
            console.error('Error during login:', error);
            setError('Network error. Please check your connection and try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const resendVerification = async () => {
        if (!email) return;
        setIsLoading(true);
        setResentMsg('');
        try {
            const res = await fetch(`${API_BASE_URL}/api/resend-verification`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: email.trim() })
            });
            const data = await res.json();
            if (res.ok) {
                setResentMsg('Verification email sent. Please check your inbox.');
            } else {
                setResentMsg(data.error || 'Failed to send verification email.');
            }
        } catch (e) {
            setResentMsg('Network error while sending verification.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (setter) => (e) => {
        setter(e.target.value);
        if (error) {
            setError('');
        }
    };

    // CSS styles as a string
    const styles = `
        @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-20px) rotate(3deg); }
        }
        
        @keyframes fadeInUp {
            from {
                opacity: 0;
                transform: translateY(30px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }
        
        @keyframes slideInRight {
            from {
                opacity: 0;
                transform: translateX(30px);
            }
            to {
                opacity: 1;
                transform: translateX(0);
            }
        }
        
        @keyframes shake {
            0%, 100% { transform: translateX(0); }
            25% { transform: translateX(-5px); }
            75% { transform: translateX(5px); }
        }

        .background-image {
            background-image: url('https://plus.unsplash.com/premium_photo-1681487837141-7aa3cb70a040?q=80&w=880&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D');
            background-size: cover;
            background-position: center;
            background-attachment: fixed;
        }

        .blur-overlay {
            backdrop-filter: blur(10px);
            background: rgba(255, 255, 255, 0.1);
        }

        .glass-card {
            background: rgba(255, 255, 255, 0.9);
            backdrop-filter: blur(20px);
            border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-float-reverse { animation: float 8s ease-in-out infinite reverse; }
        .animate-float-slow { animation: float 7s ease-in-out infinite; }
        .animate-fadeInUp { animation: fadeInUp 0.8s ease-out; }
        .animate-slideInRight { animation: slideInRight 0.6s ease-out; }
        .animate-shake { animation: shake 0.5s ease-in-out; }
    `;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: styles }} />
            
            <div className="flex flex-col min-h-screen">
                <Header />
                
                {/* Main Content */}
                <div className="flex-1 background-image relative overflow-hidden">
                    {/* Blur Overlay */}
                    <div className="absolute inset-0 blur-overlay"></div>
                    
                    {/* Enhanced animated background elements */}
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                        <div className="absolute -top-40 -left-40 w-80 h-80 rounded-full bg-gradient-to-r from-blue-400/30 to-blue-600/30 blur-3xl animate-float" />
                        <div className="absolute top-20 right-10 w-60 h-60 rounded-full bg-gradient-to-r from-blue-500/25 to-blue-700/25 blur-3xl animate-float-reverse" />
                        <div className="absolute bottom-20 left-20 w-72 h-72 rounded-full bg-gradient-to-r from-blue-300/20 to-blue-500/20 blur-3xl animate-float-slow" />
                        <div className="absolute top-1/2 left-1/3 w-40 h-40 rounded-full bg-gradient-to-r from-white/10 to-blue-100/10 blur-2xl animate-pulse" />
                    </div>

                    <div className="flex items-stretch min-h-screen relative z-10">
                        {/* Left Hero Section */}
                        <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-12">
                            <div className="max-w-lg text-center space-y-8 animate-fadeInUp">
                                {/* Logo only (no circular background) */}
                                <div className="relative">
                                    <div className="relative mx-auto flex items-center justify-center drop-shadow-2xl transform hover:scale-110 transition-all duration-500">
                                        <Logo className="w-16 h-16 text-blue-700" />
                                    </div>
                                </div>

                                {/* Hero text */}
                                <div className="space-y-4">
                                    <h1 className="text-5xl font-black bg-gradient-to-r from-slate-800 via-blue-600 to-blue-800 bg-clip-text text-transparent leading-tight">
                                        Your Career.
                                        <br />
                                        <span className="text-4xl">Your Future.</span>
                                    </h1>
                                    <p className="text-lg text-slate-100 leading-relaxed drop-shadow-sm">
                                        Connect with opportunities that matter. Modern recruitment 
                                        platform built for ambitious professionals and forward-thinking companies.
                                    </p>
                                </div>

                                {/* Features */}
                                <div className="flex flex-wrap gap-4 justify-center">
                                    <div className="flex items-center gap-2 glass-card px-4 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group transform hover:-translate-y-1">
                                        <Sparkles className="w-4 h-4 text-blue-600 group-hover:animate-spin transition-all" />
                                        <span className="text-sm font-medium text-slate-700">Smart Matching</span>
                                    </div>
                                    <div className="flex items-center gap-2 glass-card px-4 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group transform hover:-translate-y-1">
                                        <Zap className="w-4 h-4 text-blue-500 group-hover:animate-bounce transition-all" />
                                        <span className="text-sm font-medium text-slate-700">Instant Connect</span>
                                    </div>
                                    <div className="flex items-center gap-2 glass-card px-4 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group transform hover:-translate-y-1">
                                        <Shield className="w-4 h-4 text-blue-700 group-hover:scale-110 transition-transform" />
                                        <span className="text-sm font-medium text-slate-700">Secure Platform</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Form Section */}
                        <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
                            <div className="w-full max-w-md">
                                {/* Form Container */}
                                <div className="glass-card shadow-2xl rounded-3xl overflow-hidden animate-slideInRight hover:shadow-3xl transition-all duration-500">
                                    <div className="p-10">
                                        {/* Form Header */}
                                        <div className="text-center mb-10 space-y-2">
                                            <h2 className="text-3xl font-bold bg-gradient-to-r from-slate-800 to-blue-600 bg-clip-text text-transparent">
                                                Welcome Back
                                            </h2>
                                            <p className="text-slate-600">Sign in to continue your journey</p>
                                            <div className="w-16 h-1 bg-gradient-to-r from-blue-500 to-blue-700 rounded-full mx-auto" />
                                        </div>

                                        <form onSubmit={handleSubmit} className="space-y-6">
                                            {/* Email Field */}
                                            <div className="relative group">
                                                <div className={`absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 opacity-0 group-hover:opacity-20 blur transition-all duration-300 ${focusedField === 'email' ? 'opacity-30' : ''}`} />
                                                <div className="relative">
                                                    <UserRound className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 transition-all duration-300 ${focusedField === 'email' ? 'text-blue-600' : 'text-slate-400'}`} />
                                                    <input
                                                        type="email"
                                                        placeholder="Enter your email"
                                                        value={email}
                                                        onChange={handleInputChange(setEmail)}
                                                        onFocus={() => setFocusedField('email')}
                                                        onBlur={() => setFocusedField('')}
                                                        required
                                                        disabled={isLoading}
                                                        className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-slate-200 bg-white/80 focus:border-blue-500 focus:bg-white focus:shadow-lg focus:outline-none transition-all duration-300 disabled:bg-slate-50 disabled:cursor-not-allowed placeholder:text-slate-400 hover:border-slate-300"
                                                        autoComplete="email"
                                                    />
                                                </div>
                                            </div>

                                            {/* Password Field */}
                                            <div className="relative group">
                                                <div className={`absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 opacity-0 group-hover:opacity-20 blur transition-all duration-300 ${focusedField === 'password' ? 'opacity-30' : ''}`} />
                                                <div className="relative">
                                                    <KeyRound className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 transition-all duration-300 ${focusedField === 'password' ? 'text-blue-600' : 'text-slate-400'}`} />
                                                    <input
                                                        type={showPassword ? 'text' : 'password'}
                                                        placeholder="Enter your password"
                                                        value={password}
                                                        onChange={handleInputChange(setPassword)}
                                                        onFocus={() => setFocusedField('password')}
                                                        onBlur={() => setFocusedField('')}
                                                        required
                                                        disabled={isLoading}
                                                        className="w-full pl-12 pr-12 py-4 rounded-xl border-2 border-slate-200 bg-white/80 focus:border-blue-500 focus:bg-white focus:shadow-lg focus:outline-none transition-all duration-300 disabled:bg-slate-50 disabled:cursor-not-allowed placeholder:text-slate-400 hover:border-slate-300"
                                                        autoComplete="current-password"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1"
                                                        tabIndex={-1}
                                                    >
                                                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="flex justify-end">
                                                <button
                                                    type="button"
                                                    onClick={() => navigate('/forgot-password')}
                                                    disabled={isLoading}
                                                    className="text-sm font-semibold text-blue-700 hover:text-blue-900 hover:underline disabled:opacity-50"
                                                >
                                                    Forgot password?
                                                </button>
                                            </div>

                                            {/* Error Message */}
                                            {error && (
                                                <div className="flex items-center gap-3 text-red-700 bg-red-50/90 backdrop-blur-sm border border-red-200 p-4 rounded-xl animate-shake">
                                                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                                                    <span className="text-sm font-medium">{error}</span>
                                                </div>
                                            )}
                                            {needsVerification && (
                                                <div className="mt-2 bg-blue-50 border border-blue-200 text-blue-800 p-4 rounded-xl">
                                                    <div className="flex items-center justify-between gap-3 flex-wrap">
                                                        <span className="text-sm">Didn't get the email?</span>
                                                        <button type="button" onClick={resendVerification} disabled={isLoading}
                                                            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                                                            Resend verification email
                                                        </button>
                                                    </div>
                                                    {resentMsg && <div className="text-sm mt-2">{resentMsg}</div>}
                                                </div>
                                            )}

                                            {/* Submit Button */}
                                            <button
                                                type="submit"
                                                disabled={isLoading || !email.trim() || !password.trim()}
                                                className="w-full relative group overflow-hidden py-4 px-6 bg-gradient-to-r from-blue-500 to-blue-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-blue-800 transform hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:hover:from-blue-500 disabled:hover:to-blue-700"
                                            >
                                                <div className="relative flex items-center justify-center">
                                                    {isLoading ? (
                                                        <>
                                                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3" />
                                                            <span>Signing In...</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span>Sign In</span>
                                                            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                                                        </>
                                                    )}
                                                </div>
                                            </button>
                                        </form>

                                        {/* Create Account Link */}
                                        <div className="text-center mt-8 pt-6 border-t border-slate-200">
                                            <p className="text-slate-600">
                                                Don't have an account?{' '}
                                                <button
                                                    onClick={() => navigate('/create-account')}
                                                    className="font-semibold bg-gradient-to-r from-blue-500 to-blue-700 bg-clip-text text-transparent hover:from-blue-700 hover:to-blue-500 transition-all duration-300 hover:underline"
                                                    disabled={isLoading}
                                                >
                                                    Create Account
                                                </button>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <Footer />
            </div>
        </>
    );
};

export default Login;